// Installation gate.
//
// The app REQUIRES installation: until it runs standalone, the UI is replaced
// by instructions to install it. That is a deliberate constraint, not a nudge:
//
//  - An ordinary browser tab has evictable storage. Losing it means losing the
//    device key, i.e. the pairing.
//  - On iOS a Home Screen app has a storage bucket of its own. A device paired
//    in Safari is invisible to the installed app, so pairing before installing
//    is wasted work and looks like data loss.
//
// Chromium exposes `beforeinstallprompt`, which we capture and replay from our
// own button. Safari has no programmatic install, so iOS gets written steps.

const DEV_HOSTS = ["localhost", "127.0.0.1", "[::1]"];

let _deferredPrompt = null;
let _onChange = null;

function isStandalone() {
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true // iOS
    );
  } catch {
    return false;
  }
}

function isIOS() {
  const ua = navigator.userAgent || "";
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isAndroid() {
  return /Android/i.test(navigator.userAgent || "");
}

// Every iOS browser can add to the Home Screen since iOS 16.4 - Chrome, Edge
// and Firefox each expose it from their own menu. This flag no longer decides
// WHETHER installation is possible, only which menu to describe: Safari's
// Share button versus a three-dot menu.
function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent || "";
  return !/CriOS|FxiOS|EdgiOS|OPiOS|mercury/i.test(ua);
}

// Local development runs in an ordinary tab. Restricted to loopback hosts, so
// a deployed build is always gated.
function isDevHost() {
  try {
    return DEV_HOSTS.includes(location.hostname);
  } catch {
    return false;
  }
}

function isIPad() {
  const ua = navigator.userAgent || "";
  return /iPad/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

const Install = {
  isStandalone,
  isIOS,
  isIPad,
  isIOSSafari,
  isAndroid,
  isDevHost,

  /**
   * Where Safari puts the Share button, which is the one thing iOS users have
   * to find. iPhone: bottom toolbar. iPad: top toolbar, near the address bar.
   */
  shareButtonLocation() {
    return isIPad() ? "top" : "bottom";
  },

  /**
   * Jump to the same page in Safari. iOS honours the (undocumented, but long
   * standing) x-safari-https: scheme, which lets a user in Chrome/Firefox/Edge
   * reach the one browser that can Add to Home Screen without copying URLs
   * around. There is no way to detect success, so the UI keeps the manual
   * "Open in Safari" menu route visible alongside it.
   */
  openInSafari() {
    try {
      window.location.href = `x-safari-${location.href}`;
      return true;
    } catch {
      return false;
    }
  },

  /** Copy the current URL - last resort if the Safari jump does nothing. */
  async copyLink() {
    const url = location.href;
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      // Clipboard API needs a secure context and permission; fall back to the
      // legacy path so this still works on plain http during testing.
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        ta.remove();
        return ok;
      } catch {
        return false;
      }
    }
  },

  /** True when the app may run: installed, or a loopback dev build. */
  isAllowedToRun() {
    return isStandalone() || isDevHost();
  },

  /** Wire up platform events. Safe to call once at boot. */
  init(onChange) {
    _onChange = onChange;
    window.addEventListener("beforeinstallprompt", (e) => {
      // Suppress the browser's mini-infobar; we present our own button.
      e.preventDefault();
      _deferredPrompt = e;
      _onChange?.();
    });
    window.addEventListener("appinstalled", () => {
      _deferredPrompt = null;
      _onChange?.();
    });
  },

  /** True when the browser handed us a prompt we can replay. */
  canPrompt() {
    return !!_deferredPrompt;
  },

  /**
   * Replay the captured prompt. Must be called from a user gesture.
   * @returns {Promise<"accepted"|"dismissed"|"unavailable">}
   */
  async prompt() {
    if (!_deferredPrompt) return "unavailable";
    try {
      _deferredPrompt.prompt();
      const { outcome } = await _deferredPrompt.userChoice;
      _deferredPrompt = null; // single use
      _onChange?.();
      return outcome === "accepted" ? "accepted" : "dismissed";
    } catch {
      _deferredPrompt = null;
      return "unavailable";
    }
  },

  /** Steps for platforms with no programmatic install. */
  manualSteps() {
    if (isIOS()) {
      return isIOSSafari()
        ? [
          'Tap the Share button in the toolbar - or the "..." menu, then Share.',
          'Scroll the list, or tap More, and choose "Add to Home Screen".',
          "Tap Add, then open Tide Authenticator from your Home Screen.",
        ]
        : [
          'Tap the "..." menu (three dots), then Share.',
          'Scroll the list, or tap More, and choose "Add to Home Screen".',
          "Tap Add, then open Tide Authenticator from your Home Screen.",
        ];
    }
    if (isAndroid()) {
      return [
        "Open the browser menu (three dots).",
        'Choose "Install app", or "Add to Home screen" if you do not see it.',
        "Confirm, then open Tide Authenticator from your home screen.",
      ];
    }
    return [
      "Open this page in Chrome or Edge - other desktop browsers cannot install apps.",
      "Click the install icon at the right of the address bar.",
      "Confirm Install, then open Tide Authenticator from the new window.",
    ];
  },
};

export { Install };
